import {isNumber, isString} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {makeSizeConstants} from '../../../../libs/shared/ui/src/styles/styleHelpers';

/*
 * Props.
 */

interface AvatarInitialsProps {
  className?: string;
  size: VisualSizesEnum | number;
  textColor: string;
  isBold?: boolean;
  value: string | number;
  lengthOverride?: number;
}

/*
 * Style.
 */

const initialsSizes = makeSizeConstants(fontSizes.tiny, fontSizes.tiny, fontSizes.small, fontSizes.veryLarge);
const initialsWeights = makeSizeConstants(fontWeights.bold, fontWeights.semibold);
const lineHeights = makeSizeConstants(16, 20, 30, 48);
const characterLimits = makeSizeConstants(1, 1, 2);

interface InitialsDivStyleProps {
  $fontSize: string;
  $lineHeight: number;
  $size: VisualSizesEnum | number;
  $textColor: string;
  $isBold?: boolean;
  $value: string | number;
}

const StyledInitialsDiv = styled.div<InitialsDivStyleProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  color: ${(p) => p.$textColor};
  font-size: ${(p) => p.$fontSize};
  font-weight: ${(p) => getWeight(p)};

  text-align: center;
  line-height: ${(p) => p.$lineHeight}px;
`;

function getWeight(props: InitialsDivStyleProps) {
  // Check if we should always be bold.
  if (props.$isBold) {
    return fontWeights.bold;
  }

  if (props.$size === 100) {
    return fontWeights.semibold;
  }

  // Otherwise, it depends on the size.
  return isNumber(props.$size) ? fontWeights.bold : initialsWeights[props.$size];
}

/*
 * Component.
 */

export const AvatarInitials: FC<AvatarInitialsProps> = (props) => {
  const {size, className, lengthOverride} = props;

  const displayValue = buildDisplayValue(props, lengthOverride);
  const [fontSize, lineHeight] = computeFontProps(size);

  return (
    <StyledInitialsDiv
      className={className}
      $fontSize={fontSize}
      $lineHeight={lineHeight}
      $size={props.size}
      $textColor={props.textColor}
      $isBold={props.isBold}
      $value={props.value}
      aria-hidden="true"
    >
      {displayValue}
    </StyledInitialsDiv>
  );
};

function computeFontProps(size: number | VisualSizesEnum): [string, number] {
  if (isNumber(size)) {
    if (size === 30) {
      return ['18px', size];
    }
    if (size === 100) {
      return ['40px', size];
    }

    return [`${size / 2}px`, size];
  }

  return [initialsSizes[size], lineHeights[size]];
}

function buildDisplayValue(props: AvatarInitialsProps, lengthOverride?: number) {
  const {size, value} = props;

  // String value. Limit the number of characters.
  if (isString(value)) {
    return value.slice(0, isNumber(size) ? 2 : lengthOverride ?? characterLimits[size]).toUpperCase();
  }

  // Number value. Limit the number.
  return Math.max(Math.min(value, 99), 0);
}
